import {parseApiInstant} from "core/format/dateFormat";
import type {MyBooking, SponsorBookingState} from "./SponsorApi";
import {rangeLengthInDays} from "./sponsorBooking";

/**
 * What an advertiser is shown about their OWN bookings (epic #441) — the rules, with no React in sight
 * (epic #26 mandate), so the list renders identically whichever component ends up hosting it.
 *
 * Three decisions live here rather than in the markup, because all three are about disclosure or about
 * money rather than about layout:
 *
 *  - a `rejectionReason` is shown only while the booking is actually REJECTED. The api keeps the column
 *    populated after a rejection, so a booking that later moves state still carries the old operator
 *    note; rendering it from the raw field would tell an advertiser their live campaign was refused.
 *  - a `paymentDueAt` is shown only while the booking is the one waiting to be paid. That field also
 *    survives into APPROVED and beyond, so rendering it raw would put a payment deadline on a booking
 *    that has already been charged.
 *  - whether "Pay now" is offered at all. Since kitecorp/transferify-api-service#1362 money moves after
 *    approval, and the api will take it only from an AWAITING_PAYMENT booking inside its window. The ui
 *    asks the same question, so the button appears exactly where the request would be accepted.
 */

/** One row of the advertiser's own bookings, ready for a dumb view to render. */
export interface MyBookingRow {
	consumerWithdrawal?: {name: string; email: string} | null;
	bookingId: string;
	state: SponsorBookingState;
	headline: string;
	startsOn: string;
	endsOn: string;
	/** Inclusive day count of the booked range — the number the `zile()` plural rule is fed. */
	days: number;
	amountMinor: number;
	currency: string;
	impressions: number;
	/**
	 * The operator's words, verbatim, or `null` when there is nothing to explain. Never paraphrased: it
	 * is the whole explanation an advertiser gets for a refused campaign.
	 */
	rejectionReason: string | null;
	/** First creative, used as the row's thumbnail; `null` when the booking somehow carries none. */
	coverImageUrl: string | null;
	/**
	 * The deadline to pay by, while this is the booking waiting to be paid; `null` in every other state.
	 *
	 * Kept as the api's own string rather than a formatted one: the FORMATTING needs a locale and a
	 * zone, which belong to the view, and this module owes nothing to the i18n layer.
	 */
	payBy: string | null;
	/** True when "Pay now" may be offered: approved, unpaid, and the deadline has not passed. */
	payable: boolean;
}

/**
 * The states in which the advertiser has already been charged.
 *
 * Named as a set rather than tested inline because two surfaces ask it: the row's own rendering, and
 * the sentence shown when a checkout is refused because the payment already landed.
 */
const PAID_STATES: ReadonlySet<SponsorBookingState> = new Set<SponsorBookingState>([
	"APPROVED",
	"ACTIVE",
	"ENDED",
	"WITHDRAWN",
]);

/** Whether this booking has been paid for: approved and scheduled, running, or finished. */
export function isPaidState(state: SponsorBookingState): boolean {
	return PAID_STATES.has(state);
}

/**
 * The state the retired `PENDING_PAYMENT` becomes.
 *
 * DEPLOY-WINDOW ONLY, and removable the day the api has deployed
 * kitecorp/transferify-api-service#1362. This ui ships first, so between the two deploys the old api
 * still writes `PENDING_PAYMENT` on a submission. Its migration `V40_17_0` then moves every one of
 * those rows to `PENDING_REVIEW` on exactly this reasoning: an unpaid submission under the new flow is
 * a submission awaiting review. This does the same thing on the read side, one deploy earlier.
 *
 * Without it such a row reaches a chip and a label keyed BY STATE, both of which answer `undefined`:
 * the advertiser gets an unstyled, wordless chip on a booking that is really waiting for review.
 */
const RETIRED_PENDING_PAYMENT = "PENDING_PAYMENT";

function currentState(state: SponsorBookingState): SponsorBookingState {
	// Compared as a string because the union no longer names it, which is the whole point: nothing in
	// the ui's grammar may branch on the retired value except this one line that erases it.
	return (state as string) === RETIRED_PENDING_PAYMENT ? "PENDING_REVIEW" : state;
}

/**
 * Maps one wire payload to its row.
 *
 * `now` is an argument rather than a call to `Date.now()` inside, so a whole LIST is judged against one
 * instant and a test can pin the two sides of a deadline against literals.
 */
export function toMyBookingRow(booking: MyBooking, now: Date = new Date()): MyBookingRow {
	// Normalised FIRST, so every rule below (the chip, the label, the reason, the button) sees one
	// vocabulary rather than each having to know about a state the api is about to stop sending.
	const state = currentState(booking.state);
	// Only this state is still waiting on money. The field outlives it on the wire, and a deadline
	// printed beside a paid booking would read as a second charge coming.
	const awaitingPayment = state === "AWAITING_PAYMENT";
	const payBy = awaitingPayment ? booking.paymentDueAt : null;
	/*
	 * Read as UTC, never in the reader's zone: `paymentDueAt` is an offset-free api `LocalDateTime`, so
	 * a browser in Bucharest reading it locally would judge the window three hours early and take the
	 * button away while the api would still have accepted the payment.
	 *
	 * A null deadline makes the booking UNPAYABLE rather than always-payable. The api refuses to leave a
	 * booking AWAITING_PAYMENT without one, so this pairing should not exist, and if it ever does, the
	 * safe reading is the one that does not offer a button the api answers 409 to.
	 */
	const due = parseApiInstant(payBy);
	return {
		consumerWithdrawal: booking.consumerWithdrawal ?? null,
		bookingId: booking.bookingId,
		state,
		headline: booking.headline,
		startsOn: booking.startsOn,
		endsOn: booking.endsOn,
		days: rangeLengthInDays(booking.startsOn, booking.endsOn),
		amountMinor: booking.amountMinor,
		currency: booking.currency,
		impressions: booking.impressions,
		rejectionReason: state === "REJECTED" ? booking.rejectionReason : null,
		coverImageUrl: booking.imageUrls[0] ?? null,
		payBy,
		payable: awaitingPayment && due !== null && due.toMillis() > now.getTime(),
	};
}

/**
 * Maps the whole list. Order is the api's (newest first) and is not re-sorted here.
 *
 * The clock is read ONCE and handed to every row, so a list cannot contradict itself by judging its
 * first row against a different instant than its last.
 */
export function toMyBookingRows(
	bookings: readonly MyBooking[], now: Date = new Date(),
): MyBookingRow[] {
	return bookings.map(booking => toMyBookingRow(booking, now));
}

/** Apply the server-confirmed transition without dropping the open receipt dialog's contact details. */
export function withWithdrawnBooking(rows: readonly MyBookingRow[], bookingId: string): MyBookingRow[] {
	return rows.map(row => row.bookingId === bookingId ? {...row, state: "WITHDRAWN", payable: false} : row);
}

/** The row a bookmark identifies, or null when it does not belong to the loaded account. */
export function findMyBookingRow(
	rows: readonly MyBookingRow[], bookingId: string | null,
): MyBookingRow | null {
	if (bookingId === null) {
		return null;
	}
	return rows.find(row => row.bookingId === bookingId) ?? null;
}
